import { useState } from 'react';
import { ActionIcon, Menu, Stack, Text, Badge, Group, ScrollArea, Box, Center, Loader, Tooltip } from '@mantine/core';
import { IconBell, IconMessageCircle } from '@tabler/icons-react';
import { useReleases } from '../../hooks/useReleases';
import { Release, ReleaseItemType } from '../../types/release';
import { formatDistanceToNow } from 'date-fns';
import { FeedbackModal } from '../Support/FeedbackModal';

const getColorForType = (type: ReleaseItemType) => {
  switch (type) {
    case 'feature':
      return 'green';
    case 'improvement':
      return 'blue';
    case 'fix':
      return 'orange';
  }
};

export const ReleaseNotificationBell = () => {
  const { releases, unreadCount, loading, fetchReleases, markAsViewed } = useReleases();
  const [feedbackModalOpened, setFeedbackModalOpened] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);

  const handleOpen = async () => {
    // Fetch releases when opening dropdown
    await fetchReleases();
    // Mark as viewed
    await markAsViewed();
  };

  const handleFeedbackClick = () => {
    setMenuOpened(false);
    setTimeout(() => setFeedbackModalOpened(true), 0);
  };

  return (
    <>
      <Menu
        shadow="md"
        width={400}
        position="bottom-end"
        onOpen={handleOpen}
        closeOnItemClick={false}
        opened={menuOpened}
        onChange={setMenuOpened}
      >
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color={unreadCount > 0 ? "violet" : "gray"}
            size="lg"
            aria-label="Release notifications"
          >
            <IconBell size={20} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Box px="xs" pt="xs">
            <Group justify="space-between" mb="xs">
              <Text fw={500} size="xs" c="dimmed">What's new</Text>
              <Tooltip label="Send feedback">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={handleFeedbackClick}
                >
                  <IconMessageCircle size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {loading ? (
              <Center p="xl">
                <Loader size="sm" />
              </Center>
            ) : releases.length === 0 ? (
              <Box p="md">
                <Text size="sm" c="dimmed" ta="center">
                  No updates yet
                </Text>
              </Box>
            ) : (
              <ScrollArea.Autosize mah={500}>
                <Stack gap="md">
                  {releases.map((release) => (
                    <ReleaseSection key={release.id} release={release} />
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Box>
        </Menu.Dropdown>
      </Menu>

      <FeedbackModal
        opened={feedbackModalOpened}
        onClose={() => setFeedbackModalOpened(false)}
      />
    </>
  );
};

const ReleaseSection = ({ release }: { release: Release }) => {
  const releaseDate = new Date(release.releaseDate);
  const timeAgo = formatDistanceToNow(releaseDate, { addSuffix: true });

  return (
    <Box
      p="sm"
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">
          {release.version}
        </Text>
        <Text size="xs" c="dimmed">
          {timeAgo}
        </Text>
      </Group>

      <Stack gap="xs">
        {release.items.map((item, index) => (
          <Box key={index}>
            <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Badge size="xs" variant="light" color={getColorForType(item.type)} style={{ flexShrink: 0, marginTop: 2 }}>
                {item.type}
              </Badge>
              <Text size="sm" fw={500} style={{ flex: 1 }}>
                {item.title}
              </Text>
            </Box>
            <Text size="xs" c="dimmed" mt={4}>
              {item.description}
            </Text>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
